/** Admin · client orgs — per-company economics rollup (GET /admin/stats). Click a row to drill in. */
import { useState } from "react";
import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ADMIN_ROLES,
  CREDIT_COSTS,
  TEST_ORG_OTP_CODE,
  adminCan,
  type AdminRole,
  type BillingCurrency,
  type OrgKind,
} from "@talenthop/shared";
import { api } from "../../lib/api.js";
import { useAuth } from "../../lib/AuthContext.js";
import { currencyMinor, inrCompact } from "../../lib/format.js";
import { Modal, apiErrorText } from "./adminUi.js";
import { KIND_LABEL, KIND_PILL } from "./orgKind.js";

interface OrgRollup {
  id: string;
  name: string;
  /** 0032 rollup: revenue is credit purchases in the org's OWN currency (minor units) + a ₹-eq axis. */
  currency: BillingCurrency;
  revenueMinor: number;
  revenueInrEq: number;
  cogsInr: number;
  /** null when the org collected nothing this cycle — a margin over ₹0 is meaningless, not −100%. */
  marginPct: number | null;
  verified: number;
  /** Unexpired credit units: whether this org can actually do anything today. */
  walletUnits: number;
  isTest: boolean;
  /** Set when the org was sold through a distributor partner (organizations.parent_org_id). */
  sourcedByOrgId: string | null;
  sourcedByName: string | null;
}

interface StatsResponse {
  stats: {
    /** null when no cash was collected MTD — a margin over ₹0 cash is meaningless. */
    grossMarginPct: number | null;
    orgs: OrgRollup[];
  };
}

/**
 * GET /orgs — the full tenant list. Used for the id→kind lookup (the stats rollup doesn't carry
 * kind) AND to surface any org the economics rollup omits (e.g. a `platform`-type org, excluded by
 * `where o.type <> 'platform'`) so nothing a caller created can become invisible/unreachable here.
 */
interface OrgSummary {
  id: string;
  name: string;
  kind?: OrgKind;
  isDistributor?: boolean;
  parentOrgId?: string | null;
  /** 0041: a test org. Listed like any other, but absent from every figure on this page. */
  isTest?: boolean;
}
interface OrgsResponse {
  orgs: OrgSummary[];
}

interface CreatedOrgResponse {
  org: { id: string; name: string };
}

const KIND_OPTS: { value: OrgKind; label: string }[] = [
  { value: "end_customer", label: "End customer — hires (FTE or staff-aug)" },
  { value: "vendor", label: "Vendor — staffing firm that fulfils staff-aug" },
];

/** Every paying org lives on this page; the chips only narrow HOW it was sold. */
type SourceFilter = "all" | "direct" | "partner";
const SOURCE_CHIPS: { value: SourceFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "direct", label: "Direct" },
  { value: "partner", label: "Via partner" },
];

const asAdminRole = (role: string | null | undefined): AdminRole | null =>
  role && (ADMIN_ROLES as readonly string[]).includes(role) ? (role as AdminRole) : null;

export function ClientOrgs(): ReactNode {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { me } = useAuth();
  // Creating an org is a client_orgs write (Owner or Admin); marking one a TEST org is Owner-only,
  // because it hands every candidate under it a guessable code and removes it from the numbers.
  // The API enforces both — this only decides whether the control is worth rendering.
  const role = asAdminRole(me?.role);
  const canCreate = role !== null && adminCan(role, "client_orgs", true);
  const canMarkTest = canCreate && role === "owner";

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["admin-stats"],
    queryFn: () => api.get<StatsResponse>("/admin/stats"),
  });

  // Kind isn't in the economics rollup — pull the full tenant list from /orgs and index by id.
  const { data: orgsData } = useQuery({
    queryKey: ["orgs"],
    queryFn: () => api.get<OrgsResponse>("/orgs"),
  });
  const allOrgs = orgsData?.orgs ?? [];
  const byId = new Map<string, OrgSummary>(allOrgs.map((o) => [o.id, o]));

  const [source, setSource] = useState<SourceFilter>("all");
  const matchesSource = (sourcedBy: string | null): boolean =>
    source === "all" || (source === "partner" ? sourcedBy !== null : sourcedBy === null);

  const rollup = data?.stats.orgs ?? [];
  const orgs = rollup.filter((o) => matchesSource(o.sourcedByOrgId));
  // Any org that exists but the economics rollup doesn't return — shown as a row (with no cycle
  // economics) so it stays visible and reachable rather than silently dropping out of the console.
  // Since 0041 that set includes every TEST org, by design: uncounted, never hidden.
  const rollupIds = new Set(rollup.map((o) => o.id));
  const missingOrgs = allOrgs.filter(
    (o) => !rollupIds.has(o.id) && matchesSource(o.parentOrgId ?? null),
  );
  const testCount = allOrgs.filter((o) => o.isTest).length;

  const totalVerified = orgs.reduce((s, o) => s + o.verified, 0);
  // Cross-currency total only makes sense on the ₹-equivalent axis (FX_TO_INR, rollup-only).
  const totalRevenueInrEq = orgs.reduce((s, o) => s + o.revenueInrEq, 0);

  // --- create org ---
  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState("");
  const [kind, setKind] = useState<OrgKind>("end_customer");
  const [staffAugEnabled, setStaffAugEnabled] = useState(false);
  // 0031: billing currency is now a deliberate choice — it locks the only price book this org will
  // ever see — plus the distributor flag + share.
  const [currency, setCurrency] = useState("INR");
  const [isDist, setIsDist] = useState(false);
  const [distPct, setDistPct] = useState(5);
  // 0041: creation-only. There is no equivalent control anywhere else in the console, and there
  // must not be — the column cannot be changed after this form submits.
  const [isTest, setIsTest] = useState(false);

  const resetForm = (): void => {
    setName("");
    setKind("end_customer");
    setStaffAugEnabled(false);
    setCurrency("INR");
    setIsDist(false);
    setDistPct(5);
    setIsTest(false);
  };

  const create = useMutation({
    mutationFn: () =>
      api.post<CreatedOrgResponse>("/orgs", {
        name: name.trim(),
        // type and region are schema-required columns nothing branches on: no API path reads
        // organizations.type beyond excluding the platform org, and residency is a deployment
        // fact rather than a per-tenant switch. Sent as fixed defaults, never asked of an operator.
        type: "employer",
        region: "in-central",
        kind,
        staffAugEnabled: kind === "end_customer" && staffAugEnabled,
        currency,
        ...(isDist ? { distSharePct: distPct } : {}),
        // Only ever sent when the Owner ticked it — never defaulted true, never sent on any
        // other request. The API refuses it for a non-Owner regardless.
        isTest: canMarkTest && isTest,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin-stats"] });
      void queryClient.invalidateQueries({ queryKey: ["orgs"] });
      resetForm();
      setCreateOpen(false);
    },
  });

  const hasRows = orgs.length > 0 || missingOrgs.length > 0;

  /** Name plus the facts that change how an operator reads the row: test, vendor, partner. */
  const orgCell = (id: string, orgName: string, currencyCode?: string): ReactNode => {
    const summary = byId.get(id);
    return (
      <>
        <span style={{ fontWeight: 600 }}>{orgName}</span>
        {/* First badge, deliberately: whether the row is real outranks everything else about it. */}
        {summary?.isTest && (
          <span className="pill r" style={{ marginLeft: 6 }} title="Known OTP · never counted in revenue or margin (wallet and usage are real)">
            Test
          </span>
        )}
        {summary?.kind === "vendor" && (
          <span className={KIND_PILL.vendor} style={{ marginLeft: 6 }}>
            {KIND_LABEL.vendor}
          </span>
        )}
        {summary?.isDistributor && (
          <span className="pill n" style={{ marginLeft: 6 }}>
            Partner
          </span>
        )}
        {currencyCode && (
          <div className="mut sm" style={{ fontWeight: 400 }}>
            bills in {currencyCode}
          </div>
        )}
      </>
    );
  };

  return (
    <section>
      <div className="page-h">
        <div>
          <h1>Client organizations</h1>
          <div className="sub">
            Per-company economics — wallet, verified, revenue, spend, margin · {allOrgs.length || "…"} orgs ·
            live from the API
            {testCount > 0 && (
              <>
                {" · "}
                <b>
                  {testCount} test org{testCount === 1 ? "" : "s"}
                </b>{" "}
                listed but excluded from every figure below
              </>
            )}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button className="btn sm primary" onClick={() => setCreateOpen(true)}>
            New org
          </button>
          <span className="pill n">Admin</span>
        </div>
      </div>

      {isLoading && <div className="card pad mut sm">Loading client orgs…</div>}

      {isError && (
        <div className="banner crit">
          <span>{apiErrorText(error, "Couldn't load client orgs. Please retry in a moment.")}</span>
        </div>
      )}

      {!isLoading && !isError && !hasRows && source === "all" && (
        <div className="card pad">
          <p className="mut sm" style={{ marginTop: 0 }}>
            No client orgs yet — provision one to get started.
          </p>
          <button className="btn primary" onClick={() => setCreateOpen(true)}>
            New org
          </button>
        </div>
      )}

      {!isLoading && !isError && (hasRows || source !== "all") && (
        <>
          <div className="grid g4" style={{ marginBottom: 16 }}>
            <div className="card pad stat">
              <div className="l">Client orgs</div>
              <div className="n tnum">{orgs.length}</div>
              <div className="d">billable tenants</div>
            </div>
            <div className="card pad stat">
              <div className="l">Verified · MTD</div>
              <div className="n tnum">{totalVerified.toLocaleString("en-IN")}</div>
              <div className="d">identity checks this cycle</div>
            </div>
            <div className="card pad stat">
              <div className="l">Revenue · MTD (₹-eq)</div>
              <div className="n tnum">{inrCompact(totalRevenueInrEq)}</div>
              <div className="d">credit purchases · ≈ FX rollup</div>
            </div>
            <div className="card pad stat">
              <div className="l">Blended margin</div>
              {/* null = no cash collected this cycle — "—", never a green 0% over real COGS. */}
              <div
                className={`n tnum ${
                  data?.stats.grossMarginPct == null
                    ? ""
                    : data.stats.grossMarginPct >= 0
                      ? "good"
                      : "crit"
                }`}
              >
                {data?.stats.grossMarginPct == null ? "—" : `${data.stats.grossMarginPct}%`}
              </div>
              <div className="d">across all orgs · MTD</div>
            </div>
          </div>

          <div className="card">
            <div className="card-h">
              <h3>Per-company analytics · this cycle</h3>
              <span className="mut sm">Click a row to drill in</span>
            </div>
            <div style={{ display: "flex", gap: 8, padding: "12px 20px 0" }}>
              {SOURCE_CHIPS.map((c) => (
                <button
                  key={c.value}
                  className={`btn sm ${source === c.value ? "primary" : "ghost"}`}
                  onClick={() => setSource(c.value)}
                >
                  {c.label}
                </button>
              ))}
            </div>
            <div className="tbl-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Organization</th>
                    <th>Sourced by</th>
                    <th>Wallet</th>
                    <th>Verified</th>
                    <th>Revenue</th>
                    <th>Spend (COGS)</th>
                    <th>Margin %</th>
                  </tr>
                </thead>
                <tbody>
                  {!hasRows && (
                    <tr>
                      <td colSpan={7} className="mut sm">
                        No orgs sold {source === "partner" ? "through a partner" : "directly"} yet.
                      </td>
                    </tr>
                  )}
                  {orgs.map((o) => (
                    <tr key={o.id} className="clickable" onClick={() => navigate(`/admin/orgs/${o.id}`)}>
                      <td>{orgCell(o.id, o.name, o.currency)}</td>
                      <td>
                        {o.sourcedByName ? (
                          <span className="pill t">{o.sourcedByName}</span>
                        ) : (
                          <span className="mut sm">Direct</span>
                        )}
                      </td>
                      <td className="tnum">
                        {o.walletUnits.toLocaleString("en-IN")}
                        {/* Under one interview the org is stalled — answer that inline, not on drill-in. */}
                        {o.walletUnits < CREDIT_COSTS.interview && (
                          <span className="pill r" style={{ marginLeft: 6 }}>
                            low
                          </span>
                        )}
                      </td>
                      <td className="tnum">{o.verified.toLocaleString("en-IN")}</td>
                      {/* Revenue in the org's OWN currency — the ₹-eq axis lives on the Revenue page.
                          A test org's purchases are excluded from every money figure, so print "—"
                          rather than a ₹0 that reads like a real reporting number. */}
                      <td className="tnum">
                        {o.isTest ? (
                          <span className="mut sm" title="Test organisation — purchases are never counted as revenue">—</span>
                        ) : (
                          currencyMinor(o.revenueMinor, o.currency)
                        )}
                      </td>
                      <td className="tnum">{inrCompact(o.cogsInr)}</td>
                      <td>
                        {o.marginPct === null ? (
                          <span className="mut sm">—</span>
                        ) : (
                          <span className={o.marginPct >= 60 ? "pill v" : o.marginPct > 0 ? "pill r" : "pill n"}>
                            {o.marginPct}%
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                  {/* Orgs the economics rollup omits (test orgs, platform-type) — visible & reachable,
                      no cycle data. Each says WHY it has no numbers rather than showing bare dashes. */}
                  {missingOrgs.map((o) => (
                    <tr key={o.id} className="clickable" onClick={() => navigate(`/admin/orgs/${o.id}`)}>
                      <td>
                        {orgCell(o.id, o.name)}
                        <div className="mut sm" style={{ fontWeight: 400 }}>
                          {o.isTest
                            ? "Test organisation"
                            : "Not in the billing rollup"}
                        </div>
                      </td>
                      <td>
                        {o.parentOrgId ? (
                          <span className="pill t">{byId.get(o.parentOrgId)?.name ?? "Partner"}</span>
                        ) : (
                          <span className="mut sm">Direct</span>
                        )}
                      </td>
                      <td className="mut sm">—</td>
                      <td className="mut sm">—</td>
                      <td className="mut sm">—</td>
                      <td className="mut sm">—</td>
                      <td className="mut sm">—</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="disclaimer">
            <b>Tenant isolation.</b> Operators see health, billing & margin metadata only — never a client's
            candidate content, except where that client escalates a human review.
          </div>
        </>
      )}

      {createOpen && (
        <Modal title="New organization" onClose={() => setCreateOpen(false)}>
          <p className="mut sm" style={{ marginTop: 0 }}>
            Provision a tenant. <b>Kind</b> decides its role in staff-augmentation — an end customer hires,
            a vendor fulfils.
          </p>
          <div className="field" style={{ marginBottom: 10 }}>
            <label>Name</label>
            <input
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Meridian Tech GCC"
            />
          </div>
          <div className="field" style={{ marginBottom: 10 }}>
            <label>Kind</label>
            <select value={kind} onChange={(e) => setKind(e.target.value as OrgKind)}>
              {KIND_OPTS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          {/* A vendor FULFILS staff-aug for someone else, so the entitlement is meaningless on one. */}
          {kind === "end_customer" && (
            <div className="field" style={{ marginBottom: 10 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={staffAugEnabled}
                  onChange={(e) => setStaffAugEnabled(e.target.checked)}
                  style={{ width: 16, height: 16 }}
                />
                <span>Places contractors through staffing vendors</span>
              </label>
              <p className="mut sm" style={{ margin: "4px 0 0 24px" }}>
                Leave off for a customer that hires directly — they will see no staff-aug controls at all.
                You can change this later on the org's page.
              </p>
            </div>
          )}
          <div className="grid g2" style={{ marginBottom: 12 }}>
            <div className="field">
              <label>Billing currency</label>
              <select value={currency} onChange={(e) => setCurrency(e.target.value)}>
                {["INR", "USD", "GBP", "EUR"].map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
              <div className="mut sm" style={{ marginTop: 4 }}>
                Their whole experience prices in this — locked at creation.
              </div>
            </div>
            <div className="field">
              <label>Distributor partner</label>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input type="checkbox" checked={isDist} onChange={(e) => setIsDist(e.target.checked)} />
                <span className="mut sm">earns</span>
                <input
                  className="input tnum"
                  style={{ width: 64 }}
                  type="number"
                  min={1}
                  max={50}
                  value={distPct}
                  disabled={!isDist}
                  onChange={(e) => setDistPct(Math.max(1, Math.min(50, Number(e.target.value) || 5)))}
                />
                <span className="mut sm">% of child-org purchases</span>
              </div>
            </div>
          </div>
          {/* Owner-only, and absent (not merely disabled) for anyone else — an Admin cannot act on
              it, so showing it greyed out would only advertise a capability they do not have. */}
          {canMarkTest && (
            <div className="field" style={{ marginBottom: 12 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={isTest}
                  onChange={(e) => setIsTest(e.target.checked)}
                  style={{ width: 16, height: 16 }}
                />
                <span>Test organisation</span>
              </label>
              <p className="mut sm" style={{ margin: "4px 0 0 24px" }}>
                Every candidate here checks in with the known code <b>{TEST_ORG_OTP_CODE}</b>, and the org is
                left out of revenue, verified counts, spend and margin everywhere in the console.{" "}
                <b>This cannot be changed later</b> — to convert, create a new org.
              </p>
            </div>
          )}
          {isTest && canMarkTest && (
            <div className="banner warn" style={{ marginBottom: 12 }}>
              <span>
                <b>{name.trim() || "This org"}</b> will be created as a permanent test tenant. Do not use it for
                a real client.
              </span>
            </div>
          )}
          {create.isError && (
            <div className="banner crit" style={{ marginBottom: 12 }}>
              <span>{apiErrorText(create.error, "Couldn't create the org. Please retry.")}</span>
            </div>
          )}
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button className="btn ghost" onClick={() => setCreateOpen(false)}>
              Cancel
            </button>
            <button
              className="btn primary"
              disabled={name.trim().length < 2 || create.isPending}
              onClick={() => create.mutate()}
            >
              {create.isPending ? "Creating…" : "Create org"}
            </button>
          </div>
        </Modal>
      )}
    </section>
  );
}
